import { useForm, useModal } from '@refinedev/antd';
import dayjs from 'dayjs';
import { useRulesContext } from 'contexts/rulesContext/RulesContext';
import apiEndpoint from 'core/common/apiEndPoints';
import RuleChangeGroupContent from 'pages/ruleChange/ruleGroup/RuleGroupFields';
import openNotificationWithIcon from 'core/utils/notification';
import constants from 'core/constants/Constants';
import CustomModal from 'components/modal/modal';
import getFooterButton from 'core/utils/getFooterButton';
import { useTranslate } from '@refinedev/core';
import { useEffect } from 'react';

const RuleGroupForm = ({ id, mode, onDone }) => {
  const { rulePlanDataObj } = useRulesContext();
  const planStartDate = dayjs(rulePlanDataObj?.rule_plan_start_date);
  const planEndDate = dayjs(rulePlanDataObj?.rule_plan_end_date);
  const t = useTranslate();
  const statusModal = useModal();

  const { onFinish, formProps, form, saveButtonProps } = useForm({
    resource: apiEndpoint.ruleGroupDraft,
    action: id ? 'edit' : 'create',
    id,
    successNotification: false,
    errorNotification: false,
    onMutationSuccess: () => {
      statusModal.close();
      onDone();
    },
    onMutationError: (error) => {
      const message = error?.message || t('notifications.somethingError');
      openNotificationWithIcon(
        constants.error,
        t('notifications.mutationError'),
        message
      );
    },
  });

  const oldIsActive = formProps?.initialValues?.results?.rule_group_is_active;

  useEffect(() => {
    if (id) {
      const startDate = dayjs(
        formProps?.initialValues?.results?.rule_group_start_date
      );
      const endDate = dayjs(
        formProps?.initialValues?.results?.rule_group_end_date
      );
      form.setFieldsValue({ rule_group_date: [startDate, endDate] });
    } else if (
      rulePlanDataObj?.rule_plan_start_date &&
      rulePlanDataObj?.rule_plan_end_date
    ) {
      form.setFieldsValue({ rule_group_date: [planStartDate, planEndDate] });
    }
  }, [
    id,
    formProps?.initialValues,
    rulePlanDataObj?.rule_plan_start_date,
    rulePlanDataObj?.rule_plan_end_date,
  ]);

  const onFinishHandler = async (data: any) => {
    if (
      id &&
      !statusModal.modalProps.open &&
      oldIsActive !== data?.rule_group_is_active
    ) {
      statusModal.show();
      return;
    }
    const [start, end] = data?.rule_group_date || [dayjs(), dayjs()];
    await onFinish({
      ...data,
      rule_plan: rulePlanDataObj?.unique_id,
      rule_group_start_date: dayjs(start).format('YYYY-MM-DD'),
      rule_group_end_date: dayjs(end).format('YYYY-MM-DD'),
    });
  };

  return (
    <>
      <RuleChangeGroupContent
        formData={{ formProps }}
        eventHandler={{ onFinishHandler }}
        stateData={{
          ruleGroupDate: [planStartDate, planEndDate],
          groupHasRules: 0,
        }}
        staticData={{ type: mode }}
        configData={{ disabled: false }}
      />
      <CustomModal
        openModal={statusModal.modalProps.open}
        hideModal={() => statusModal.close()}
        title={t('common.note')}
        message={
          oldIsActive
            ? t('ruleGroup.messages.ruleGroupInactiveAlert')
            : t('ruleGroup.messages.ruleGroupActiveAlert')
        }
        footer={getFooterButton(
          oldIsActive,
          saveButtonProps,
          form,
          onFinishHandler,
          t
        )}
      />
    </>
  );
};

export default RuleGroupForm;
